import {Injectable, Injector, OnDestroy} from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import {EMPTY, from, mergeMap, Observable, Subject, take, tap, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {AuthService} from "../_services/auth.service";
import {NotificationService} from "../_services/notification.service";
import {GlobalStore} from "../global.store";
import {OfflineQueueService} from "../_services/offline-queue.service";
import {AccessPolicyService} from "../_services/access-policy.service";
import {CompanyWorkspaceService} from "../_services/company-workspace.service";


@Injectable()
export class ErrorInterceptor implements HttpInterceptor, OnDestroy {
  private notifService: NotificationService;

  private readonly destroy$ = new Subject<void>()

  constructor(private injector: Injector) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        this.notifService ??= this.injector.get(NotificationService);

        if (err.error instanceof Blob && err.error.type === 'application/json') {
          return from(err.error.text()).pipe(
            mergeMap(text => {
              try {
                const json = JSON.parse(text);
                err = new HttpErrorResponse({
                  ...err,
                  error: json
                });
              } catch {
              }
              return this.handleError(request, err);
            })
          );
        }

        return this.handleError(request, err);
      })
    );
  }

  private handleError(request: HttpRequest<any>, err: HttpErrorResponse): Observable<never> {
    if (err.status === 0) {
      this.notifService.handlePingTimeout();
      const subject = new Subject<HttpEvent<any>>();
      this.injector.get(OfflineQueueService).queueRequest(request, subject);
      return subject.asObservable() as never;
    } else if (err.status === 208) {
      this.notifService.errorNotification(
        'This item has been modified, please open the form again or refresh the page'
      );
    } else if (err.status === 400) {
      if (err.error === 'User already exists') {
        this.notifService.errorNotification('Such user already exists');
      } else if (err.error?.error === 'Project is archived') {
        if (request.method.toLowerCase() === 'get') {
          return this.redirectToNotFound();
        }
        this.notifService.errorNotification('This project is archived. Changes cannot be saved');
      } else if (err.error?.error === 'Archived section is not accessible') {
        if (request.method.toLowerCase() === 'get') {
          return this.redirectToNotFound();
        }
        this.notifService.errorNotification('This section is archived. Changes cannot be saved');
      } else if (err.error?.error) {
        this.notifService.errorNotification(err.error.error);
      }
    } else if (err.status === 401) {
      this.injector.get(AuthService).logout(true);
    } else if (err.status === 403) {
      if (this.isWelcomeRoute()) {
        this.injector.get(CompanyWorkspaceService).clearStoredCompanyContext();
        this.injector.get(GlobalStore).resetState();
        return throwError(() => err);
      }

      if (request.url.includes('/access-policy')
        && !this.injector.get(AccessPolicyService).shouldLoadForCurrentContext()) {
        return throwError(() => err);
      }

      if (request.params.has('SkipNotFoundNotification')) {
        return throwError(() => err);
      }

      window.location.href = '/not-found';
    } else if (err.status === 404) {
      if (request.params.has('SkipNotFoundNotification')) {
        return throwError(() => err);
      } else if (request.method.toLowerCase() === 'get') {
        return this.injector.get(GlobalStore).companyId$.pipe(
          take(1),
          tap(companyId => {
            window.location.href = companyId
              ? `company/${companyId}/not-found`
              : '/not-found';
          }),
          switchMap(() => EMPTY)
        );
      } else {
        this.notifService.errorNotification(`Error. ${err.error?.error || 'Not found'}`);
      }
    } else if (err.status === 500) {
      if (['post', 'put'].includes(request.method.toLowerCase()))
        this.notifService.errorNotification('Error. Failed to save data');
      else
        this.notifService.errorNotification('Error. Internal server error');
    }

    return throwError(() => err);
  }

  private redirectToNotFound(): Observable<never> {
    return this.injector.get(GlobalStore).companyId$.pipe(
      take(1),
      tap(companyId => {
        window.location.href = companyId
          ? `/company/${companyId}/not-found`
          : '/not-found';
      }),
      switchMap(() => EMPTY)
    );
  }

  private isWelcomeRoute(): boolean {
    return window.location.pathname === '/welcome';
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
